import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  SelectChangeEvent,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Drawer,
  SvgIcon,
} from "@mui/material";
import {
  GridViewOutlined,
  ViewListRounded,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  Close,
  LocalFlorist as LocalFloristIcon,
} from "@mui/icons-material";
import { ReactComponent as FilterBtnIcon } from "../../assets/icons/filter-btn-icon.svg";
import CustomBreadcrumbs from "../../common/components/breadcrumb-component-new/breadcrumb-component-new";
import MobilePlannersFilters from "./components/MobilePlannersFilters";
import PlannersFilters from "./components/PlannersFilters";
import QuoteModal from "../../common/components/quote-modal-component/QuoteModal";
import PlannerCard from "./components/PlannerCard";
import { Planner, PlannersFilterState } from "./preneed-planners.types";
import PlannerSuccessModal from "../../common/components/planner-success-modal/PlannerSuccessModal";
import {
  getFilteredPlanners,
  getInitialPlanners,
  plannerAutoSuggestions,
  getPlannerBySearchText,
  requestQuote,
} from "../../api/preneed-planners.api";
import PaginationComponentNew from "../../common/components/pagination-component-new/pagination-component-new";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useDebounce } from "use-debounce";
import SearchSort from "./components/search-sort";
import "./preneed-planners.scss";

const DEFAULT_FILTERS: PlannersFilterState = {
  userLat: 0,
  userLong: 0,
  // priceRange: [0, 7000],
  // distanceRange: [0, 100],
  ratings: [],
  isPremium: false,
  sortBy: "distance_low_to_high",
};
interface Suggestion {
  display: string;
  type: "planner" | "location" | "partner";
  data: any;
}

export default function PreneedPlannersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(
    sessionStorage.getItem("plannerSearchText") || ""
  );
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isListView, setIsListView] = useState(true);
  const [loading, setLoading] = useState(false);
  const [planners, setPlanners] = useState<Planner[] | null>(null);
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedPlanner, setSelectedPlanner] = useState<Planner | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [plannersFilters, setPlannersFilters] =
    useState<PlannersFilterState>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(itemsPerPage);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  const [debouncedSearchValue] = useDebounce(searchValue, 300);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [preSearchPlanners, setPreSearchPlanners] = useState<Planner[] | null>(
    null
  );
  const isInitialMount = React.useRef(true);
  const isSearching = React.useRef(false);
  const hasInitializedSearchValue = React.useRef(false);
  const [preSearchTotalCount, setPreSearchTotalCount] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [sortSource, setSortSource] = useState<"filters" | "searchSort">(
    "filters"
  );

  const isPreproductionEnv =
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) === "preproduction";
  const isProductionEnv =
    (process.env.REACT_APP_ENV || process.env.NODE_ENV) === "production";

  useEffect(() => {
    const storedSearches = localStorage.getItem("recent_searches");
    if (storedSearches) {
      setRecentSearches(JSON.parse(storedSearches));
    }
  }, []);

  const addRecentSearch = (searchTerm: string) => {
    const updatedSearches = [
      searchTerm,
      ...recentSearches.filter((search) => search !== searchTerm),
    ].slice(0, 5);
    setRecentSearches(updatedSearches);
    localStorage.setItem("recent_searches", JSON.stringify(updatedSearches));
  };

  const removeRecentSearch = (searchTerm: string) => {
    const updatedSearches = recentSearches.filter(
      (search) => search !== searchTerm
    );
    setRecentSearches(updatedSearches);
    localStorage.setItem("recent_searches", JSON.stringify(updatedSearches));
  };

  const handleRecentSearchClick = (searchTerm: string) => {
    setSearchValue(searchTerm);
    setShowSuggestions(true);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("recent_searches");
  };

  const handleRequestQuote = (planner: Planner) => {
    setSelectedPlanner(planner);
    setModalOpen(true);
  };

  const sortOptions = [
    { value: "price_high_to_low", label: "Price(High-Low)" },
    { value: "price_low_to_high", label: "Price(Low-High)" },
    { value: "distance_low_to_high", label: "Distance" },
    { value: "rating_high_to_low", label: "Rating" },
  ];

  const storedLocation = localStorage.getItem("user_location");
  let latitude = 0;
  let longitude = 0;

  if (storedLocation) {
    const loc = JSON.parse(storedLocation);
    latitude = loc.latitude;
    longitude = loc.longitude;
  }

  const {
    city,
    latitude: reduxLat,
    longitude: reduxLng,
  } = useSelector((state: any) => state.userLocation);

  const effectiveLat = reduxLat || latitude;
  const effectiveLng = reduxLng || longitude;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue === "") {
      handlePlannersFiltersChange(plannersFilters, 1);
    }
    setSearchValue(newValue);
    setShowSuggestions(true);
  };

  const handleSearch = async (text: string) => {
    addRecentSearch(text);
    if (!preSearchPlanners) {
      setPreSearchPlanners(planners);
      setPreSearchTotalCount(totalCount);
    }
    try {
      setLoading(true);
      isSearching.current = true;
      setCurrentPage(1);
      const response = await getPlannerBySearchText({ text });
      if (response.data.success) {
        setPlanners(response.data.data.data);
        setTotalCount(response.data.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching planners by search text:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestion: Suggestion) => {
    addRecentSearch(suggestion.display);
    if (!preSearchPlanners) {
      setPreSearchPlanners(planners);
      setPreSearchTotalCount(totalCount);
    }
    setSearchValue(suggestion.display);
    setSuggestions([]);
    setShowSuggestions(false);
    try {
      setLoading(true);
      setCurrentPage(1);
      let response;
      if (suggestion.type === "planner") {
        response = await getPlannerBySearchText({
          businessName: suggestion.data.name,
          type: suggestion.data.type,
        });
      } else {
        response = await getPlannerBySearchText({
          type: "location",
          city: suggestion.data.city,
          state: suggestion.data.state,
        });
      }
      if (response.data.success) {
        setPlanners(response.data.data.data);
        setTotalCount(response.data.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching planners by search text:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    if (!debouncedSearchValue.trim() || !showSuggestions) {
      setSuggestions([]);
      return;
    }
    try {
      setSuggestionLoading(true);
      const response = await plannerAutoSuggestions(debouncedSearchValue);
      if (response.data.success) {
        const { planners, locations } = response.data.data;
        const plannerSuggestions = planners.map((p: any) => ({
          display: `${p.name}`,
          type: "planner",
          data: p,
        }));
        const locationSuggestions = locations.map((l: any) => ({
          display: `${l.full}`,
          type: "location",
          data: l,
        }));

        const combined = [...plannerSuggestions, ...locationSuggestions];
        const uniqueSuggestions = Array.from(
          new Map(combined.map((item) => [item.display, item])).values()
        );
        setSuggestions(uniqueSuggestions);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setSuggestionLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [debouncedSearchValue, showSuggestions]);

  useEffect(() => {
    if (!hasInitializedSearchValue.current) {
      hasInitializedSearchValue.current = true;
      sessionStorage.setItem("plannerSearchText", searchValue);
      return;
    }

    sessionStorage.setItem("plannerSearchText", searchValue);
    if (searchValue === "") {
      handlePlannersFiltersChange(plannersFilters, 1);
    }
  }, [searchValue]);

  const handleSortChange = (event: SelectChangeEvent) => {
    const newSortValue = event.target.value;
    setSortSource("searchSort");

    handlePlannersFiltersChange({ ...plannersFilters, sortBy: newSortValue });
  };

  const filteredPLanners = useMemo(() => {
    let result = planners || [];
    switch (plannersFilters?.sortBy) {
      case "price_low_to_high":
        result = [...result].sort((a, b) => (a.startingPrice ?? 0) - (b.startingPrice ?? 0));
        break;
      case "price_high_to_low":
        result = [...result].sort((a, b) => (b.startingPrice ?? 0) - (a.startingPrice ?? 0));
        break;
      case "distance_low_to_high":
        result = [...result].sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
        break;
      // case "rating":
      //   result = [...result].sort((a, b) => b?.avgRating - a?.avgRating);
      //   break;
    }

    return result;
  }, [plannersFilters?.sortBy, planners]);

  const renderComingSoon = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 8, md: 12 },
        px: 4,
        textAlign: "center",
        background:
          "linear-gradient(135deg, rgba(16, 65, 69, 0.04) 0%, rgba(159, 127, 44, 0.04) 100%)",
        borderRadius: "16px",
        border: "1px solid rgba(16, 65, 69, 0.08)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 8px 32px 0 rgba(16, 65, 69, 0.03)",
        maxWidth: "600px",
        margin: "2rem auto",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #104145 0%, #1c5b60 100%)",
          color: "#fff",
          mb: 3,
          boxShadow: "0 6px 20px rgba(16, 65, 69, 0.25)",
          animation: "pulse-flower 2.5s infinite ease-in-out",
          "@keyframes pulse-flower": {
            "0%": {
              transform: "scale(0.96)",
              boxShadow: "0 6px 15px rgba(16, 65, 69, 0.2)",
            },
            "50%": {
              transform: "scale(1.04)",
              boxShadow: "0 6px 25px rgba(16, 65, 69, 0.35)",
            },
            "100%": {
              transform: "scale(0.96)",
              boxShadow: "0 6px 15px rgba(16, 65, 69, 0.2)",
            },
          },
        }}
      >
        <LocalFloristIcon sx={{ fontSize: 34 }} />
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: "#104145",
          mb: 1.5,
          letterSpacing: "-0.5px",
        }}
      >
        Coming Soon
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: "rgba(16, 65, 69, 0.75)",
          maxWidth: "400px",
          lineHeight: 1.6,
          fontSize: "16px",
        }}
      >
        Planners will be listed here soon.
      </Typography>
    </Box>
  );

  const renderMobileListings = () => {
    if (isPreproductionEnv || isProductionEnv) {
      return renderComingSoon();
    }
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
          }}
        >
          <CircularProgress
            size="2.5rem"
            sx={{
              color: "#104145",
            }}
          />
        </Box>
      );
    }

    if (filteredPLanners?.length > 0) {
      return (
        <>
          {filteredPLanners.map((planner, index) => {
            const preloadIndex = mobileVisibleCount - 2;
            const showSentinel = index === preloadIndex;

            return (
              <React.Fragment key={planner.id}>
                <PlannerCard
                  planner={planner}
                  view={isListView ? "list" : "grid"}
                  onRequestQuote={handleRequestQuote}
                />
                {showSentinel && (
                  <Box
                    ref={loadMoreRef}
                    sx={{
                      height: 1,
                      opacity: 0,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}

          {isLoadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
              }}
            >
              <CircularProgress
                size="2.5rem"
                className="spinner-details-page__spinner"
              />
            </Box>
          )}
        </>
      );
    }

    return (
      <Typography
        variant="body1"
        sx={{ mt: 10, color: "text.secondary", textAlign: "center" }}
      >
        {infoMessage || "No planners found"}
      </Typography>
    );
  };

  const renderDesktopListings = () => {
    if (isPreproductionEnv || isProductionEnv) {
      return renderComingSoon();
    }
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 2,
            color: "#104145",
            width: "100%",
            gridColumn: "1 / -1",
          }}
        >
          <CircularProgress
            size="3rem"
            className="spinner-details-page__spinner"
          />
        </Box>
      );
    }

    if (filteredPLanners?.length > 0) {
      return filteredPLanners.map((planner) => (
        <PlannerCard
          key={planner.id}
          planner={planner}
          view={isListView ? "list" : "grid"}
          onRequestQuote={handleRequestQuote}
        />
      ));
    }

    return (
      <Typography
        variant="body1"
        sx={{
          mt: 10,
          color: "text.secondary",
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gridColumn: "1 / -1",
        }}
      >
        {infoMessage || "No planners found"}
      </Typography>
    );
  };

  const toggleView = (viewType: "list" | "grid") => {
    setIsListView(viewType === "list");
  };

  const fetchInitialPlanners = async (
    lat: number,
    lng: number,
    page: number = 1,
    options?: { skipLoading?: boolean }
  ) => {
    const savedSearchText = sessionStorage.getItem("plannerSearchText");
    if (savedSearchText) return;
    try {
      if (!options?.skipLoading) {
        setLoading(true);
      }

      const params: any = {
        userLat: lat,
        userLng: lng,
        page: page,
        pageSize: itemsPerPage,
      };

      const response = await getInitialPlanners(params);
      if (response.data?.success) {
        const data = response.data.data;
        setTotalCount(response.data.data.totalCount || 0);
        setPlanners(data.data);
      }
    } catch (error) {
      console.error("ERROR FETCHING INITIAL PLANNERS::", error);
    } finally {
      if (!options?.skipLoading) {
        setLoading(false);
      }
    }
  };

  const handlePlannersFiltersChange = async (
    newFilters: PlannersFilterState,
    pageNumber: number = 1
  ) => {
    const savedSearchText = sessionStorage.getItem("plannerSearchText");
    if (savedSearchText) return;
    setCurrentPage(pageNumber);
    setPlannersFilters(newFilters);
    try {
      setLoading(true);
      const areFiltersActive =
        (newFilters.priceRange?.[0] ?? 0) > 0 ||
        (newFilters.priceRange?.[1] ?? 7000) < 7000 ||
        (newFilters.distanceRange?.[0] ?? 0) > 0 ||
        (newFilters.distanceRange?.[1] ?? 100) < 100 ||
        (newFilters.ratings && newFilters.ratings.length > 0) ||
        newFilters.sortBy ||
        newFilters.isTopRated ||
        newFilters.isPremium;

      if (areFiltersActive) {
        const params: any = {
          userLat: effectiveLat,
          userLng: effectiveLng,
          ...(newFilters.priceRange && {
            minPrice: newFilters.priceRange[0],
            maxPrice: newFilters.priceRange[1],
          }),
          ...(newFilters.distanceRange && {
            minDistance: Math.round(newFilters.distanceRange[0] * 1.60934),
            maxDistance: Math.round(newFilters.distanceRange[1] * 1.60934),
          }),
          ...(newFilters.ratings?.length && { rating: newFilters.ratings }),
          ...(newFilters.sortBy && { sortBy: newFilters.sortBy }),
          ...(newFilters.isTopRated && { isTopRated: newFilters.isTopRated }),
          ...(newFilters.isPremium && { isPremium: newFilters.isPremium }),
          page: pageNumber,
          pageSize: itemsPerPage,
        };

        const response = await getFilteredPlanners(params);
        const data = response.data.data;
        setTotalCount(response.data.data.totalCount || 0);
        setPlanners(data.data);
        if (data.data.length === 0) setInfoMessage("No planners found");
        else setInfoMessage("");
      } else {
        await fetchInitialPlanners(effectiveLat, effectiveLng, pageNumber, {
          skipLoading: true,
        });
      }
    } catch (error) {
      console.error("Planner filter fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only run when we actually have coordinates
    if (effectiveLat && effectiveLng) {
      // Reset to first page when location changes
      setCurrentPage(1);
      handlePlannersFiltersChange(plannersFilters, 1);
    }
  }, [effectiveLat, effectiveLng]);

  useEffect(() => {
    const savedSearchText = sessionStorage.getItem("plannerSearchText");
    const savedSuggestion = sessionStorage.getItem("searchPlannerSuggestion");
    const parsedSuggestion = savedSuggestion
      ? JSON.parse(savedSuggestion)
      : null;
    if (savedSearchText && !parsedSuggestion) {
      handleSearch(savedSearchText);
    } else if (parsedSuggestion) {
      handleSuggestionClick(parsedSuggestion);
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (isSearching.current) {
      isSearching.current = false;
      return;
    }

    handlePlannersFiltersChange(plannersFilters, currentPage);
  }, [currentPage]);

  useEffect(() => {
    const clearFilters = location.state?.clearFilters;
    const savedState = sessionStorage.getItem("preneedPlannersState");
    if (clearFilters) {
      sessionStorage.removeItem("preneedPlannersState");
      sessionStorage.removeItem("plannerSearchText");
      setSearchValue("");
      if (window.history.state?.clearFilters) {
        window.history.replaceState({}, document.title);
      }
    } else if (savedState) {
      const {
        searchValue: savedSearchValue,
        plannersFilters: savedPlannersFilters,
        planners: savedPlanners,
        currentPage: savedCurrentPage,
        totalCount: savedTotalCount,
      } = JSON.parse(savedState);
      sessionStorage.removeItem("preneedPlannersState");
      setSearchValue(savedSearchValue || "");
      setPlannersFilters(savedPlannersFilters || DEFAULT_FILTERS);
      setPlanners(savedPlanners);
      setCurrentPage(savedCurrentPage || 1);
      setTotalCount(savedTotalCount || 0);
    }
  }, []);

  useEffect(() => {
    const stateToSave = {
      searchValue,
      plannersFilters,
      planners,
      currentPage,
      totalCount,
    };
    if (searchValue || Object.values(plannersFilters).some(Boolean)) {
      sessionStorage.setItem(
        "preneedPlannersState",
        JSON.stringify(stateToSave)
      );
    }
  }, [searchValue, plannersFilters, planners, currentPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          !isLoadingMore &&
          mobileVisibleCount < filteredPLanners.length
        ) {
          setIsLoadingMore(true);
          setMobileVisibleCount((prev) => prev + itemsPerPage);
          setIsLoadingMore(false);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [isLoadingMore, mobileVisibleCount, filteredPLanners.length]);

  useEffect(() => {
    setCurrentPage(1);
    setMobileVisibleCount(itemsPerPage);
  }, [plannersFilters?.sortBy]);

  return (
    <Container
      className="preneed-page"
      sx={{
        padding: isMobile ? "0 !important" : "",
      }}
    >
      {isMobile ? (
        <Container sx={{ py: 2, px: 0.5 }} className="mobile-view-container">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
            className="mobile-breadcrumbs-toggle"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#104145",
                gap: 1,
              }}
              className="mobile-breadcrumbs"
            >
              <ArrowBackIosNewIcon onClick={() => navigate(-1)} />

              <Typography
                sx={{
                  fontSize: isMobile ? "1rem" : "1.2rem",
                  color: "#000",
                }}
              >
                List of All Planners
              </Typography>
            </Box>

            <Box className="view-toggle">
              <Button
                onClick={() => toggleView("list")}
                variant={isListView ? "contained" : "outlined"}
                className={`toggle-btn ${isListView ? "active" : ""}`}
              >
                <ViewListRounded fontSize="small" />
              </Button>
              <Button
                onClick={() => toggleView("grid")}
                variant={!isListView ? "contained" : "outlined"}
                className={`toggle-btn ${!isListView ? "active" : ""}`}
              >
                <GridViewOutlined fontSize="small" />
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              backgroundColor: "#F2F2F2",
              padding: 1,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setFilterDrawerOpen(true)}
              sx={{
                flexShrink: 0,
                bgcolor: "#d1d7d8",
                color: "#104145",
                textTransform: "capitalize",
                borderRadius: "8px",
                border: "none",
              }}
            >
              <SvgIcon
                component={FilterBtnIcon}
                inheritViewBox
                sx={{
                  width: "14px",
                  marginRight: "0.3rem",
                }}
              />
              Filters
            </Button>
            <SearchSort
              searchValue={searchValue}
              sortValue={plannersFilters?.sortBy || ""}
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              sortOptions={sortOptions}
              placeholder="Search Planner name or Location"
              suggestions={suggestions}
              onSuggestionClick={handleSuggestionClick}
              loading={suggestionLoading}
              onSearch={() => handleSearch(searchValue)}
              recentSearches={recentSearches}
              onClearRecentSearches={clearRecentSearches}
              onRecentSearchClick={handleRecentSearchClick}
              onRemoveRecentSearch={removeRecentSearch}
            />
          </Box>

          <Box
            className={`preneed-page__cards ${isListView
              ? "preneed-page__cards--list"
              : "preneed-page__cards--grid"
              }`}
          >
            {renderMobileListings()}
          </Box>
          <Drawer
            anchor="top"
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
          >
            <Box sx={{ p: 2, pt: 0, overflowY: "auto" }}>
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  bgcolor: "white",
                  zIndex: 1,
                  py: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Close onClick={() => setFilterDrawerOpen(false)} />
              </Box>
              <MobilePlannersFilters
                initialFilters={plannersFilters}
                onChange={handlePlannersFiltersChange}
                onCloseDrawer={() => setFilterDrawerOpen(false)}
              />
            </Box>
          </Drawer>
        </Container>
      ) : (
        <Box className="preneed-page__layout">
          <Box className="preneed-page__filters">
            <PlannersFilters
              initialFilters={plannersFilters}
              onChange={handlePlannersFiltersChange}
              sortSource={sortSource}
              setSortSource={setSortSource}
            />
          </Box>

          <Box className="preneed-page__main">
            <Box className="preneed-page__header">
              <CustomBreadcrumbs
                items={[
                  { label: "Home", to: "/home" },

                  {
                    label: "List of Funeral Homes",
                    to: "/home/list-of-funeral-homes",
                  },
                  { label: "List of All Planners" },
                ]}
              />

              <Box className="preneed-page__view-toggle">
                <Button
                  variant={isListView ? "contained" : "outlined"}
                  className={`preneed-page__toggle-btn ${isListView ? "preneed-page__toggle-btn--active" : ""
                    }`}
                  onClick={() => toggleView("list")}
                >
                  <ViewListRounded fontSize="small" />
                </Button>
                <Button
                  variant={!isListView ? "contained" : "outlined"}
                  className={`preneed-page__toggle-btn ${!isListView ? "preneed-page__toggle-btn--active" : ""
                    }`}
                  onClick={() => toggleView("grid")}
                >
                  <GridViewOutlined fontSize="small" />
                </Button>
              </Box>
            </Box>

            <Box className="preneed-page__search-sort">
              <SearchSort
                searchValue={searchValue}
                sortValue={plannersFilters?.sortBy || ""}
                onSearchChange={handleSearchChange}
                onSortChange={handleSortChange}
                sortOptions={sortOptions}
                placeholder="Search Planner name or Location"
                suggestions={suggestions}
                onSuggestionClick={handleSuggestionClick}
                loading={suggestionLoading}
                onSearch={() => handleSearch(searchValue)}
                recentSearches={recentSearches}
                onClearRecentSearches={clearRecentSearches}
                onRecentSearchClick={handleRecentSearchClick}
                onRemoveRecentSearch={removeRecentSearch}
              />
            </Box>

            <Box
              className={`preneed-page__cards ${isListView
                ? "preneed-page__cards--list"
                : "preneed-page__cards--grid"
                }`}
            >
              {renderDesktopListings()}
            </Box>
          </Box>
        </Box>
      )}
      {selectedPlanner && (
        <QuoteModal
          open={isModalOpen}
          onClose={() => setModalOpen(false)}
          title={selectedPlanner.businessName}
          description="Use this form to reach out and share your preneed planning preferences"
          partnerId={selectedPlanner.id}
          onSuccess={() => {
            setModalOpen(false);
            setShowSuccess(true);
          }}
          servicesOptions={selectedPlanner.services}
          plannerServiceCategories={selectedPlanner.serviceCategories}
          requestFn={requestQuote}
          isPlanner={true}
        />
      )}

      {selectedPlanner && (
        <PlannerSuccessModal
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          title="Successful Message"
          subtitle="Thank you"
          businessName={selectedPlanner?.businessName}
          isConsultant={false}
        />
      )}

      {totalPages > 0 && !isPreproductionEnv && !isProductionEnv && (
        <Box
          className="preneed-page__pagination"
          sx={{
            display: isMobile ? "none" : "flex",
          }}
        >
          <PaginationComponentNew
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </Box>
      )}
    </Container>
  );
}
